"use client";
import React from "react";
import Container from "../container";
import { H3, H4 } from "../headers";
import Image from "next/image";
import Room from "@/public/images/home-images/6d15218666b8593298f7db63a4da0266.webp";
import SeaAndMosque from "@/public/images/home-images/c47173380eb010cf28a82ebdb24e4f1c.webp";
import { Button } from "../ui/button";
import { useTranslations } from "next-intl";
import DefaultLink from "next/link";
import Link from "next/link";
import { useSpring, animated } from "@react-spring/web";
import { useIntersectionObserver } from "usehooks-ts";

const BenefitsSection = () => {
  const t = useTranslations();

  // Hook to detect when the first section is in view
  const { isIntersecting: inView1, ref: ref1 } = useIntersectionObserver({
    threshold: 0.1, // Trigger when 10% of the element is visible
    freezeOnceVisible: true,
  });

  // Hook to detect when the second section is in view
  const { isIntersecting: inView2, ref: ref2 } = useIntersectionObserver({
    threshold: 0.1, // Trigger when 10% of the element is visible
    freezeOnceVisible: true,
  });

  // Animation for the first section
  const animation1 = useSpring({
    opacity: inView1 ? 1 : 0,
    transform: inView1 ? "translateX(0)" : "translateX(100px)",
    config: { duration: 500 },
  });

  // Animation for the second section
  const animation2 = useSpring({
    opacity: inView2 ? 1 : 0,
    transform: inView2 ? "translateX(0)" : "translateX(-100px)",
    config: { duration: 500 },
  });

  return (
    <div className="overflow-hidden">
      <Container className="flex *:flex-1 sm:gap-20 gap-5 my-12 sm:my-24">
        {
          //@ts-ignorer
          <animated.div
            ref={ref1 as React.Ref<HTMLDivElement>}
            style={animation1 as any}
          >
            <div className="space-y-10">
              <H3 className="font-semibold text-green-main max-sm:text-center">
                {t("welcome_heading")}
              </H3>
              <H4 className="text-gray-light max-sm:text-center">
                {t("benefitsSection.title")}
              </H4>
              <ul className="text-gray-light grid max-sm:grid-cols-1 grid-cols-2 gap-2 text-xs max-sm:text-sm *:before:content-['✓'] *:before:text-lg *:flex *:gap-2 *:items-center *:z-[1]">
                <li> {t("benefitsSection.benefit1")}</li>
                <li>{t("benefitsSection.benefit2")}</li>
                <li> {t("benefitsSection.benefit3")}</li>
                <li> {t("benefitsSection.benefit4")}</li>
                <li> {t("benefitsSection.benefit5")}</li>
                <li> {t("benefitsSection.benefit6")}</li>
                <li> {t("benefitsSection.benefit7")}</li>
              </ul>
              <div className="flex gap-4 sm:gap-8 justify-center *:basis-1/2">
                {process.env.NEXT_PUBLIC_BUSINESS_URL && (
                  <Button
                    asChild
                    className="bg-green-main text-white  hover:bg-green-400 active:bg-green-400  focus:outline-none focus:ring-green-400 transition"
                  >
                    <DefaultLink
                      href={process.env.NEXT_PUBLIC_BUSINESS_URL}
                    >
                      {t("benefitsSection.addPropertyButton")}
                    </DefaultLink>
                  </Button>
                )}
                <Button
                  asChild
                  variant={"outline"}
                  className="bg-white text-gray-light border-gray-light hover:border-green-main hover:text-green-main transition"
                >
                  <Link href={`/contact-us`} prefetch>
                    {t("benefitsSection.contactUsButton")}
                  </Link>
                </Button>
              </div>
            </div>
          </animated.div>
        }
        <div className="relative max-lg:hidden" ref={ref2}>
          {
            //@ts-ignorer
            <animated.div style={animation2}>
              <div className="p-8 border border-green-main rounded-2xl">
                <Image
                  src={Room}
                  className="rounded-2xl h-[500px] object-cover"
                  alt="room"
                  loading="lazy"
                />
              </div>
              <div className="p-4 rounded-2xl bg-white absolute -bottom-14 -right-20">
                <Image
                  src={SeaAndMosque}
                  className="rounded-2xl w-[350px] h-64 object-cover"
                  alt="sea and mosque"
                  loading="lazy"
                />
              </div>
            </animated.div>
          }
        </div>
      </Container>
    </div>
  );
};

export default BenefitsSection;
